/**
 * The Data tab's pure half (spec §2) — the three decisions worth testing without a DOM.
 *
 * `DatasetRows.tsx` keeps the state and draws the table; everything here is a function of its
 * arguments, so the sort cycle, the cell formatting and the "rows 1–50 of 812" window can be
 * checked directly rather than through eight clicks.
 */
import type { DatasetRowsColumn, DatasetRowsParams, RowsSortDir } from '../../lib/api';
import { NO_VALUE } from '../../lib/format';
import i18n, { DEFAULT_LANGUAGE } from '../../lib/i18n';

/** The ordering the table is showing: a column key and a direction. */
export type SortState = Pick<DatasetRowsParams, 'sort' | 'dir'>;

/**
 * The ordering a click on `key`'s header produces.
 *
 * A newly clicked column starts ascending; the column already sorted flips. That is what
 * every table the reader has used does, and it means two clicks always get back to where
 * they started.
 */
export function sortParam(
  current: SortState | null,
  key: string,
): { sort: string; dir: RowsSortDir } {
  if (current?.sort === key) return { sort: key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { sort: key, dir: 'asc' };
}

/**
 * Columns whose numbers are **labels, not quantities**: a year grouped as "2,025" reads as
 * two thousand and twenty-five. The server sends `period` as `kind: "number"` because it is
 * right-aligned like one, so the exception lives here rather than on the wire.
 */
const YEAR_KEYS = new Set(['period', 'year']);

/**
 * One cell, printed.
 *
 * Missing is missing — null, undefined and the empty string all become the em dash §F2 uses
 * everywhere else, so an absent unit never shows as a blank the reader has to interpret.
 * Numbers are grouped in the **interface language** (§F12: `97,967.5` in English is
 * `97 967,5` in French), to at most three decimals — enough for a rate or a share, not
 * enough to print a float's noise. A `number` column carrying something non-numeric (a
 * provider sending "n/a") is printed as it stands rather than turned into an em dash, which
 * would lose what the server actually said.
 */
export function formatCell(
  value: string | number | boolean | null | undefined,
  kind: DatasetRowsColumn['kind'],
  key?: string,
): string {
  if (value === null || value === undefined || value === '') return NO_VALUE;
  if (kind === 'number' && typeof value === 'number' && Number.isFinite(value)) {
    const lang = i18n.language || DEFAULT_LANGUAGE;
    if (key && YEAR_KEYS.has(key)) {
      return new Intl.NumberFormat(lang, { useGrouping: false }).format(value);
    }
    return new Intl.NumberFormat(lang, { maximumFractionDigits: 3 }).format(value);
  }
  return String(value);
}

/**
 * The interpolation for `catalogue.record.rowsCount` — the 1-based window on screen, already
 * grouped in the interface language.
 *
 * Strings, not numbers: "rows 1–50 of 12576" is the one number on this panel a reader has to
 * take in at a glance, and it is grouped everywhere else in the portal (§F12). The member is
 * `total` rather than `count` because `count` is i18next's reserved plural selector and has to
 * stay a number if it is passed at all.
 */
export function pageLabel(
  page: number,
  size: number,
  count: number,
): { from: string; to: string; total: string } {
  const group = (n: number) => new Intl.NumberFormat(i18n.language || DEFAULT_LANGUAGE).format(n);
  if (count <= 0) return { from: group(0), to: group(0), total: group(0) };
  const from = (page - 1) * size + 1;
  return { from: group(from), to: group(Math.min(page * size, count)), total: group(count) };
}
